import { useCallback, useEffect, useState } from 'react';

import Topbar from '../components/layout/Topbar';
import Button from '../components/shared/Button';
import Input from '../components/shared/Input';
import Modal from '../components/shared/Modal';
import StatCard from '../components/shared/StatCard';
import { useToast } from '../components/shared/Toast';
import api from '../services/api';
import { useAppStore } from '../store/useAppStore';

interface Transaction {
  id: number;
  type: 'deposit' | 'withdrawal' | 'reward' | 'payment';
  amount: number;
  balance_after: number;
  description: string;
  status: string;
  created_at: string;
}

export default function Wallet(): JSX.Element {
  const { user } = useAppStore();
  const toast = useToast();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [withdrawModal, setWithdrawModal] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawLoading, setWithdrawLoading] = useState(false);

  const loadTransactions = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/wallet/transactions');
      setTransactions(response.data as Transaction[]);
    } catch (_error) {
      toast.error('Islemler yuklenemedi');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void loadTransactions();
  }, [loadTransactions]);

  const handleWithdraw = async () => {
    const amount = parseFloat(withdrawAmount);
    if (!amount || amount <= 0) {
      toast.error('Gecerli bir miktar girin');
      return;
    }
    if (amount > (user?.wallet_balance ?? 0)) {
      toast.error('Yetersiz bakiye');
      return;
    }

    try {
      setWithdrawLoading(true);
      await api.post('/wallet/withdraw', { amount });
      toast.success('Cekim talebi olusturuldu');
      setWithdrawModal(false);
      setWithdrawAmount('');
      void loadTransactions();
    } catch (err) {
      const error = err as { response?: { data?: { detail?: string } } }
      toast.error(error.response?.data?.detail ?? 'Cekim basarisiz');
    } finally {
      setWithdrawLoading(false);
    }
  };

  const getTransactionIcon = (type: string) => {
    switch (type) {
      case 'deposit': return '⬇️';
      case 'withdrawal': return '⬆️';
      case 'reward': return '🎁';
      case 'payment': return '💳';
      default: return '💰';
    }
  };

  const getTransactionColor = (type: string) => {
    switch (type) {
      case 'deposit':
      case 'reward':
        return '#00ff88';
      case 'withdrawal':
      case 'payment':
        return '#ff4444';
      default:
        return 'var(--text-primary)';
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>      <div style={{ flex: 1, overflow: 'auto' }}>
        <Topbar />
        <div style={{ flex: 1, overflow: 'auto', padding: '2rem' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
              <h1>Cuzdan</h1>
              <Button onClick={() => setWithdrawModal(true)} variant="primary">
                Para Cek
              </Button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
              <StatCard
                title="Toplam Bakiye"
                value={`${user?.wallet_balance || 0} FLZ`}
                icon="💰"
                color="#00d4ff"
              />
              <StatCard
                title="Bu Ay Kazanc"
                value="0 FLZ"
                icon="📈"
                color="#00ff88"
              />
              <StatCard
                title="Bekleyen Cekim"
                value="0 FLZ"
                icon="⏳"
                color="#ffaa00"
              />
            </div>

            <h2>Islem Gecmisi</h2>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '3rem' }}>Yukleniyor...</div>
            ) : transactions.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-secondary)' }}>
                Henuz islem bulunmuyor
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {transactions.map(tx => (
                  <div key={tx.id} className="transaction-item">
                    <div className="transaction-icon">
                      {getTransactionIcon(tx.type)}
                    </div>
                    <div className="transaction-content">
                      <h3>{tx.description}</h3>
                      <span className="transaction-time">
                        {new Date(tx.created_at).toLocaleString('tr-TR')}
                      </span>
                    </div>
                    <div
                      className="transaction-amount"
                      style={{ color: getTransactionColor(tx.type) }}
                    >
                      {tx.type === 'deposit' || tx.type === 'reward' ? '+' : '-'}
                      {tx.amount} FLZ
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <Modal
        isOpen={withdrawModal}
        onClose={() => setWithdrawModal(false)}
        title="Para Cek"
        size="sm"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          <div>
            <p style={{ color: 'var(--text-secondary)', margin: '0 0 1rem 0' }}>
              Mevcut Bakiye: <strong>{user?.wallet_balance || 0} FLZ</strong>
            </p>
            <Input
              type="number"
              label="Cekilecek Miktar (FLZ)"
              value={withdrawAmount}
              onChange={(e) => setWithdrawAmount(e.target.value)}
              fullWidth
              required
            />
          </div>
          <Button
            onClick={() => void handleWithdraw()}
            variant="primary"
            loading={withdrawLoading}
            fullWidth
          >
            Cekim Talebi Olustur
          </Button>
        </div>
      </Modal>
    </div>
  );
}
